import { useEffect, useState, useCallback } from 'react';
import { Bell } from 'lucide-react';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { NOTIFICATION_KIND_LABEL, type AppNotification } from '@/lib/notifications';
import { getNotificationPrefs, useNotificationPrefs } from '@/lib/notificationPrefs';

function timeAgo(iso: string): string {
  const diff = Math.max(0, Date.now() - new Date(iso).getTime());
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

export function NotificationBell() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState<AppNotification[]>([]);
  const [readIds, setReadIds] = useState<Set<string>>(new Set());
  const [open, setOpen] = useState(false);
  const prefs = useNotificationPrefs();

  const load = useCallback(async () => {
    if (!user) return;
    const [{ data: notifs }, { data: reads }] = await Promise.all([
      supabase.from('notifications').select('*').order('created_at', { ascending: false }).limit(30),
      supabase.from('notification_reads').select('notification_id').eq('user_id', user.id),
    ]);
    setItems((notifs || []) as AppNotification[]);
    setReadIds(new Set((reads || []).map((r: any) => r.notification_id)));
  }, [user]);

  useEffect(() => {
    if (!user) {
      setItems([]); setReadIds(new Set()); return;
    }
    load();
    const channel = supabase
      .channel('notifications-feed')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'notifications' },
        (payload) => {
          const n = payload.new as AppNotification;
          setItems((prev) => [n, ...prev].slice(0, 30));
          const p = getNotificationPrefs();
          if (p.toastsEnabled && p.kinds[n.kind] !== false) {
            toast(n.title, { description: n.body || undefined });
          }
        }
      )
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [user, load]);

  const visibleItems = items.filter((n) => prefs.kinds[n.kind] !== false);
  const unread = visibleItems.filter((n) => !readIds.has(n.id)).length;

  const markAllRead = async () => {
    if (!user) return;
    const unreadItems = visibleItems.filter((n) => !readIds.has(n.id));
    if (unreadItems.length === 0) return;
    const rows = unreadItems.map((n) => ({ notification_id: n.id, user_id: user.id }));
    const { error } = await supabase.from('notification_reads').insert(rows as any);
    if (!error) setReadIds(new Set([...readIds, ...unreadItems.map((n) => n.id)]));
  };

  const handleClick = async (n: AppNotification) => {
    if (user && !readIds.has(n.id)) {
      await supabase.from('notification_reads').insert({ notification_id: n.id, user_id: user.id } as any);
      setReadIds(new Set([...readIds, n.id]));
    }
    setOpen(false);
    if (n.link) {
      if (n.link.startsWith('http')) window.open(n.link, '_blank');
      else navigate(n.link);
    }
  };

  if (!user) return null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative h-9 w-9" aria-label="Notifications">
          <Bell className="h-5 w-5" />
          {unread > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-destructive text-destructive-foreground text-[10px] font-bold flex items-center justify-center">
              {unread > 99 ? '99+' : unread}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0">
        <div className="flex items-center justify-between px-3 py-2 border-b border-border">
          <span className="text-sm font-semibold">Notifications</span>
          {unread > 0 && (
            <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={markAllRead}>
              Mark all read
            </Button>
          )}
        </div>
        <div className="max-h-[min(24rem,60vh)] overflow-y-auto overscroll-contain">
          {visibleItems.length === 0 ? (
            <div className="px-3 py-6 text-center text-xs text-muted-foreground">No notifications yet</div>
          ) : (
            <ul className="divide-y divide-border">
              {visibleItems.map((n) => {
                const isUnread = !readIds.has(n.id);
                return (
                  <li key={n.id}>
                    <button
                      onClick={() => handleClick(n)}
                      className={`w-full text-left px-3 py-2.5 hover:bg-accent transition-colors ${isUnread ? 'bg-accent/40' : ''}`}
                    >
                      <div className="flex items-start gap-2">
                        {isUnread && <span className="mt-1.5 h-2 w-2 rounded-full bg-primary shrink-0" />}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-2">
                            <p className="text-sm font-medium truncate">{n.title}</p>
                            <span className="text-[10px] text-muted-foreground shrink-0">{timeAgo(n.created_at)}</span>
                          </div>
                          {n.body && <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{n.body}</p>}
                          {n.kind !== 'general' && (
                            <Badge variant="secondary" className="mt-1 text-[10px] h-4 px-1.5">
                              {NOTIFICATION_KIND_LABEL[n.kind] ?? n.kind}
                            </Badge>
                          )}
                        </div>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}